import {FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useGetDesignerSettings} from "../../../../../hooks/api/DesignerSettings/useGetDesignerSettings";
import {API, Endpoints, Tag} from "@brg/api";
import styled, { css } from "styled-components";
import {BTag} from "../../../../../../core/components";
import {bTag, LayoutConst} from "../../../../../../core";
import useQueryState from "../../../../../hooks/useQueryState";
import {useProfile} from "@brg/auth";
import EditIcon from "../../../../../icons/EditIcon";
import {FlexBox, Colors, useBreakpoint} from "@brg/ui";
import {CloseIcon} from "../../../../../icons";
import ArrowMoveTopIcon from "../../../../../icons/ArrowMoveTopIcon";
import {AppTester} from "../../../../../../supporting";
import {useGetProjectTags} from "../../../../../hooks/api/useProjectsByFilter";
import {useTranslation} from "react-i18next";
import {getTagProjectFilter} from "../../../../../helpers/designer/getTagProjectFilter";
import {DesignerTab} from "../../../../../constants/designerTab";
import { CollapseButton } from "shared/components/CollapseButton/CollapseButton";
import { useDispatch, useSelector } from "react-redux";
import ProfileFoldersSlice from "shared/reducers/ProfileFoldersSlice";
import {BTagsGridNew} from "../../../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { FolderT } from "../../tabs/const";
import { FoldersTabs } from "./foldersTabs";

interface DesignerTagsCloudProps {
    tabName: string;
    filterList?: any;
    tagId?: any;
    setTagId?: any;
    designerId: string;
    visible?: boolean;
    update?: any;
    folders?: FolderT[];
}

export const TagWrapper = styled.div<{isLoading?: boolean, isCompetition?: boolean, isGeo?: boolean, isCGRAVE?: boolean, isAdcrDafesNet?: boolean}>`
  background: ${({theme, isCompetition, isGeo, isCGRAVE, isAdcrDafesNet}) => isCGRAVE || isAdcrDafesNet ? '#2C2C2B' : !isCompetition ? theme.tags.cloud.background : isGeo ? '#353A3E' : '#464645'};
  // padding: 16px 16px 4px 16px;
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;  
  border-radius: 3px;
  position: relative;
  height: ${({isLoading}) => isLoading ? '102px' : 'auto'};
  margin-top: 16px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: ${({isLoading}) => isLoading ? '98px' : 'auto'};
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 16px;
    margin-top: 0;
    height: ${({isLoading}) => isLoading ? '78px' : 'auto'};
  }
`

const TagWrapperLocal = styled(TagWrapper)`
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 0;
    margin-top: 16px;
  }
`

export const ScrollWrapper = styled.div<{isLoading?: boolean, collapse?: boolean, hideBottomLine?: boolean, overflowVisible?: boolean, isAdcrDafesNet?: boolean }>`
  max-height: ${({ collapse }) => collapse ? '90px' : 'none'};
  overflow-y: ${({ overflowVisible }) => overflowVisible ? 'visible' : 'hidden'};
  margin: 0 0 -10px 0;
  margin: 0;
  width: 100%;
  display: inline-flex;
  flex-wrap: wrap;
  position: relative;
  

  ${({ hideBottomLine, theme, isAdcrDafesNet }) => !hideBottomLine && css`
      &:after {
      content: "";
      position: absolute;
      height: 2px;
      width: 100%;
      bottom: 0;
      left: 0;
      background: ${isAdcrDafesNet ? '#2C2C2B' : theme.tags.cloud.background};
    }
  `};

  
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    max-height: ${({ collapse }) => collapse ? '85px' : 'none'};
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    max-height: ${({ collapse }) => collapse ? '120px' : 'none'};
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    max-height: ${({ collapse }) => collapse ? '120px' : 'none'};
  }

  &::-webkit-scrollbar {
    width: 2px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: #F2F2F2;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #909090;
  }
`

export const EditIconWrapper = styled.div<{active?: boolean}>`
    
    position: absolute;
    right: 20px;
    cursor: pointer;
    transition: 0.2s all;
  
  svg {
    rect {
      stroke: ${({active}) => active ? Colors.galBlack100 : Colors.galBlack47};
    }
    path {
      stroke: ${({active}) => active ? Colors.galBlack100 : Colors.galBlack47};
    }
  }
  
  &:hover {
    svg {
      rect {
        stroke: ${Colors.galBlack100};
      }
      path {
        stroke: ${Colors.galBlack100};
      }
    }
  }
`

export const SplitLine = styled.div`
    
    width: 100%;
    height: 1px;
    border: 1px dashed #909090;
`

export const TagsTitleWrapper = styled(FlexBox)`
    
    align-items: center;
    font-size: 15px;
    line-height: 20px;
  
    svg {
      width: 11px;
      height: 11px;
    }
  
    span {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  
    @media (max-width: ${LayoutConst.screen.m}px) {
      font-size: 13px;
      line-height: 16px;
    }
`

export const DesignerTagsCloud: FC<DesignerTagsCloudProps> = ({
  tabName = "", 
  update, 
  folders, 
  designerId, 
  filterList = null, 
  tagId: tagIdProp, 
  setTagId, 
  visible,
 }) => {
    const profile = useProfile();
    const dispatch = useDispatch();
    const isMe = useMemo(
        () => (profile && designerId === profile.accountId),
        [profile, designerId],
    );

    const {resp: designerSettings} = useGetDesignerSettings(designerId)
    const [isEditMode, setIsEditMode] = useState(false);
    const [hiddenTagIds, setHiddenTagIds] = useState<string[]>([]);
    const tagsRef = useRef<HTMLDivElement>(null);
    const isDeziiign = AppTester.isDeziiign();
    const isCGRAVE = AppTester.isCGRAVE();
    const isAdcrDafesNet = AppTester.isAdcrDafesNet();
    const isContinuumSite = AppTester.isContinuumSite();
    const {i18n} = useTranslation();
    const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const isSmallMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const [t, setT] = useState(Date.now())
    const [open, setOpen] = useState(false);
    const filter = useMemo(() => getTagProjectFilter(tabName as DesignerTab, filterList), [tabName, filterList]);
    const [collapseVisible, setCollapseVisible] = useState(false);
    const [{ folderId}, setQueryParams ] = useQueryState();

    const [tagsEntities, isLoading] = useGetProjectTags(filter, i18n.language, t, filter === null)

    const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])

    const maxTagHeight = useMemo(() => {
      const width = window.innerWidth;
      if (width <= LayoutConst.screen.s - 1 || isSmallMobile) {
        return 130;
      }
      if (width <= LayoutConst.screen.m || isMobile) {
        return 130;
      }
      if (isMac13 || smHeight) {
        return 88;
      }
      return 92;
    }, [isSmallMobile, isMobile, isMac13, smHeight]);

    useEffect(() => {
        if (!designerSettings?.tabSettings) return setHiddenTagIds([]);
        setHiddenTagIds(designerSettings?.tabSettings?.find((setting: {type: string, hiddenTagIds: string[]}) => setting?.type === tabName)?.hiddenTagIds || [])
    }, [designerSettings, tabName])

    const onTagClick = useCallback((tagId: string, isHidden?: boolean) => {
        if (isEditMode) {
            const newTagIds = isHidden ? (hiddenTagIds?.filter(item => item !== tagId) || []) : [...hiddenTagIds, tagId];
            setHiddenTagIds(newTagIds)
            API.request()
                .endpoint(Endpoints.geograffee)
                .url('/api/Designer/UpdateDesignerTabSettings')
                .post()
                .body({type: tabName, hiddenTagIds: newTagIds})
                .send()
                .then((r) => r.json())
                .catch((e) => console.error(e))
            return;
        }
        if (setTagId) setTagId(tagId === tagIdProp ? undefined : tagId)
          setQueryParams({ folderId: undefined })
    }, [isEditMode, setHiddenTagIds, hiddenTagIds, tagIdProp, setTagId])

    const collapseButton = useMemo(() => {
      if (!collapseVisible) return null;
  
      return <CollapseButton onClick={() => setOpen(!open)} flag={open} />
    } , [open, collapseVisible])

    const visibleTags = useMemo(() => tagsEntities?.filter(tag => !hiddenTagIds?.includes(tag.tagId) && [0,1]?.includes(tag?.level)), [tagsEntities, hiddenTagIds])
    const invisibleTags = useMemo(() => tagsEntities?.filter(tag => hiddenTagIds?.includes(tag.tagId) && [0,1]?.includes(tag?.level)), [tagsEntities, hiddenTagIds])

    useEffect(() => {
      const checkHeight = () => {
        //@ts-ignore
        const tagsContainer = document.querySelector('#tags-scroll-container');
  
        if (tagsContainer) {
          //@ts-ignore
          const containerHeight = tagsContainer.scrollHeight;
          //@ts-ignore
          if (containerHeight > maxTagHeight) {
            setCollapseVisible(true)
          } else {
            setCollapseVisible(false);
          }
        }
      };

      const timer1 = setTimeout(checkHeight, 100);
      const timer2 = setTimeout(checkHeight, 300);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
         
    },[isLoading, visibleTags, maxTagHeight, isMobile, isSmallMobile, folders]);

    const hide = useMemo(() => {
      // return (filter === null || !tagsEntities || tagsEntities?.length === 0 || (visibleTags?.length === 0 && !isMe))
      return !visible || (visibleTags?.length === 0 && !isMe)
    } , [filter, tagsEntities, visible, visibleTags, isMe])

    useEffect(() => {
      dispatch(ProfileFoldersSlice.actions.setCloudButtonStatus(!hide))
    } , [hide, dispatch, filter, tagsEntities, visibleTags, isMe, visible])

    if (hide) return null;

    if (isLoading) return <TagWrapper isLoading/>

    return (
          <TagWrapperLocal isCGRAVE={isCGRAVE || isContinuumSite || isAdcrDafesNet}>
            <FlexBox>
                <FlexBox direction="column" directionMobile="column" gy={12} width='100%'>
                    <ScrollWrapper id={'tags-scroll-container'} ref={tagsRef} collapse={!open} isAdcrDafesNet={isAdcrDafesNet} overflowVisible={false}>
                        <BTagsGridNew style={{marginBottom: '12px'}} noLimitMaxHeight>
                          <FoldersTabs 
                            designerId={designerId}
                            setTagId={setTagId} 
                            tabs={folders}
                            update={update}
                            activeId={folderId}
                            isMe={isMe} 
                          />
                          {/* {folderComponent} */}
                          {visibleTags && visibleTags?.length > 0 && visibleTags?.map((tag: Tag) => <BTag
                              tag={tag}
                              onClick={() => onTagClick(tag.tagId)}
                              key={tag.tagId}
                              selected={tag.tagId === tagIdProp}
                              // @ts-ignore
                              educational={tag?.isEducationTag}
                              // @ts-ignore
                              organization={tag?.isTeamTag}
                              isShort
                              isTagsCloud
                          >
                              <TagsTitleWrapper gx={8}><span>{bTag.title(tag)}</span> {isEditMode && <CloseIcon/>}</TagsTitleWrapper>
                          </BTag>)}
                        </BTagsGridNew>
                    </ScrollWrapper>
                    {isEditMode && <>
                        <SplitLine/>
                        <ScrollWrapper>
                            <BTagsGridNew>
                                {invisibleTags?.map((tag: Tag) => <BTag
                                    tag={tag}
                                    onClick={() => onTagClick(tag.tagId, true)}
                                    key={tag.tagId}
                                    selected={tag.tagId === tagIdProp}
                                    isDeziiignStyle={isDeziiign}
                                    // @ts-ignore
                                    educational={tag?.isEducationTag}
                                    // @ts-ignore
                                    organization={tag?.isTeamTag}
                                    isShort
                                >
                                    <TagsTitleWrapper gx={8}><span>{bTag.title(tag)}</span> {isEditMode && <ArrowMoveTopIcon/>}</TagsTitleWrapper>
                                </BTag>)}
                            </BTagsGridNew>
                    </ScrollWrapper>
                    </>}
                </FlexBox>
                {isMe && <EditIconWrapper active={isEditMode} onClick={() => setIsEditMode(!isEditMode)}>
                    <EditIcon/>
                </EditIconWrapper>}
            </FlexBox>
            {collapseButton}
          </TagWrapperLocal>

    )
}import {FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useGetDesignerSettings} from "../../../../../hooks/api/DesignerSettings/useGetDesignerSettings";
import {API, Endpoints, Tag} from "@brg/api";
import styled, { css } from "styled-components";
import {BTag} from "../../../../../../core/components";
import {bTag, LayoutConst} from "../../../../../../core";
import useQueryState from "../../../../../hooks/useQueryState";
import {useProfile} from "@brg/auth";
import EditIcon from "../../../../../icons/EditIcon";
import {FlexBox, Colors, useBreakpoint} from "@brg/ui";
import {CloseIcon} from "../../../../../icons";
import ArrowMoveTopIcon from "../../../../../icons/ArrowMoveTopIcon";
import {AppTester} from "../../../../../../supporting";
import {useGetProjectTags} from "../../../../../hooks/api/useProjectsByFilter";
import {useTranslation} from "react-i18next";
import {getTagProjectFilter} from "../../../../../helpers/designer/getTagProjectFilter";
import {DesignerTab} from "../../../../../constants/designerTab";
import { CollapseButton } from "shared/components/CollapseButton/CollapseButton";
import { useDispatch, useSelector } from "react-redux";
import ProfileFoldersSlice from "shared/reducers/ProfileFoldersSlice";
import {BTagsGridNew} from "../../../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { FolderT } from "../../tabs/const";
import { FoldersTabs } from "./foldersTabs";

interface DesignerTagsCloudProps {
    tabName: string;
    filterList?: any;
    tagId?: any;
    setTagId?: any;
    designerId: string;
    visible?: boolean;
    update?: any;
    folders?: FolderT[];
}

export const TagWrapper = styled.div<{isLoading?: boolean, isCompetition?: boolean, isGeo?: boolean, isCGRAVE?: boolean, isAdcrDafesNet?: boolean}>`
  background: ${({theme, isCompetition, isGeo, isCGRAVE, isAdcrDafesNet}) => isCGRAVE || isAdcrDafesNet ? '#2C2C2B' : !isCompetition ? theme.tags.cloud.background : isGeo ? '#353A3E' : '#464645'};
  // padding: 16px 16px 4px 16px;
  padding: 16px 24px;
  width: 100%;
  box-sizing: border-box;  
  border-radius: 3px;
  position: relative;
  height: ${({isLoading}) => isLoading ? '102px' : 'auto'};
  margin-top: 16px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: ${({isLoading}) => isLoading ? '98px' : 'auto'};
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 16px;
    margin-top: 0;
    height: ${({isLoading}) => isLoading ? '78px' : 'auto'};
  }
`

const TagWrapperLocal = styled(TagWrapper)`
  @media (max-width: ${LayoutConst.screen.m}px) {
    margin-bottom: 0;
    margin-top: 16px;
  }
`

export const ScrollWrapper = styled.div<{isLoading?: boolean, collapse?: boolean, hideBottomLine?: boolean, overflowVisible?: boolean, isAdcrDafesNet?: boolean }>`
  max-height: ${({ collapse }) => collapse ? '90px' : 'none'};
  overflow-y: ${({ overflowVisible }) => overflowVisible ? 'visible' : 'hidden'};
  margin: 0 0 -10px 0;
  margin: 0;
  width: 100%;
  display: inline-flex;
  flex-wrap: wrap;
  position: relative;
  

  ${({ hideBottomLine, theme, isAdcrDafesNet }) => !hideBottomLine && css`
      &:after {
      content: "";
      position: absolute;
      height: 2px;
      width: 100%;
      bottom: 0;
      left: 0;
      background: ${isAdcrDafesNet ? '#2C2C2B' : theme.tags.cloud.background};
    }
  `};

  
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    max-height: ${({ collapse }) => collapse ? '85px' : 'none'};
  }
  
  @media (max-width: ${LayoutConst.screen.m}px) {
    max-height: ${({ collapse }) => collapse ? '120px' : 'none'};
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    max-height: ${({ collapse }) => collapse ? '120px' : 'none'};
  }

  &::-webkit-scrollbar {
    width: 2px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: #F2F2F2;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #909090;
  }
`

export const EditIconWrapper = styled.div<{active?: boolean}>`
    
    position: absolute;
    right: 20px;
    cursor: pointer;
    transition: 0.2s all;
  
  svg {
    rect {
      stroke: ${({active}) => active ? Colors.galBlack100 : Colors.galBlack47};
    }
    path {
      stroke: ${({active}) => active ? Colors.galBlack100 : Colors.galBlack47};
    }
  }
  
  &:hover {
    svg {
      rect {
        stroke: ${Colors.galBlack100};
      }
      path {
        stroke: ${Colors.galBlack100};
      }
    }
  }
`

export const SplitLine = styled.div`
    
    width: 100%;
    height: 1px;
    border: 1px dashed #909090;
`

export const TagsTitleWrapper = styled(FlexBox)`
    
    align-items: center;
    font-size: 15px;
    line-height: 20px;
  
    svg {
      width: 11px;
      height: 11px;
    }
  
    span {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  
    @media (max-width: ${LayoutConst.screen.m}px) {
      font-size: 13px;
      line-height: 16px;
    }
`

export const DesignerTagsCloud: FC<DesignerTagsCloudProps> = ({
  tabName = "", 
  update, 
  folders, 
  designerId, 
  filterList = null, 
  tagId: tagIdProp, 
  setTagId, 
  visible,
 }) => {
    const profile = useProfile();
    const dispatch = useDispatch();
    const isMe = useMemo(
        () => (profile && designerId === profile.accountId),
        [profile, designerId],
    );

    const {resp: designerSettings} = useGetDesignerSettings(designerId)
    const [isEditMode, setIsEditMode] = useState(false);
    const [hiddenTagIds, setHiddenTagIds] = useState<string[]>([]);
    const tagsRef = useRef<HTMLDivElement>(null);
    const isDeziiign = AppTester.isDeziiign();
    const isCGRAVE = AppTester.isCGRAVE();
    const isAdcrDafesNet = AppTester.isAdcrDafesNet();
    const isContinuumSite = AppTester.isContinuumSite();
    const {i18n} = useTranslation();
    const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const isSmallMobile = useBreakpoint(LayoutConst.screen.s - 1);
    const [t, setT] = useState(Date.now())
    const [open, setOpen] = useState(false);
    const filter = useMemo(() => getTagProjectFilter(tabName as DesignerTab, filterList), [tabName, filterList]);
    const [collapseVisible, setCollapseVisible] = useState(false);
    const [{ folderId}, setQueryParams ] = useQueryState();

    const [tagsEntities, isLoading] = useGetProjectTags(filter, i18n.language, t, filter === null)

    const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])

    const maxTagHeight = useMemo(() => {
      const width = window.innerWidth;
      if (width <= LayoutConst.screen.s - 1 || isSmallMobile) {
        return 130;
      }
      if (width <= LayoutConst.screen.m || isMobile) {
        return 130;
      }
      if (isMac13 || smHeight) {
        return 88;
      }
      return 92;
    }, [isSmallMobile, isMobile, isMac13, smHeight]);

    useEffect(() => {
        if (!designerSettings?.tabSettings) return setHiddenTagIds([]);
        setHiddenTagIds(designerSettings?.tabSettings?.find((setting: {type: string, hiddenTagIds: string[]}) => setting?.type === tabName)?.hiddenTagIds || [])
    }, [designerSettings, tabName])

    const onTagClick = useCallback((tagId: string, isHidden?: boolean) => {
        if (isEditMode) {
            const newTagIds = isHidden ? (hiddenTagIds?.filter(item => item !== tagId) || []) : [...hiddenTagIds, tagId];
            setHiddenTagIds(newTagIds)
            API.request()
                .endpoint(Endpoints.geograffee)
                .url('/api/Designer/UpdateDesignerTabSettings')
                .post()
                .body({type: tabName, hiddenTagIds: newTagIds})
                .send()
                .then((r) => r.json())
                .catch((e) => console.error(e))
            return;
        }
        if (setTagId) setTagId(tagId === tagIdProp ? undefined : tagId)
          setQueryParams({ folderId: undefined })
    }, [isEditMode, setHiddenTagIds, hiddenTagIds, tagIdProp, setTagId])

    const collapseButton = useMemo(() => {
      if (!collapseVisible) return null;
  
      return <CollapseButton onClick={() => setOpen(!open)} flag={open} />
    } , [open, collapseVisible])

    const visibleTags = useMemo(() => tagsEntities?.filter(tag => !hiddenTagIds?.includes(tag.tagId) && [0,1]?.includes(tag?.level)), [tagsEntities, hiddenTagIds])
    const invisibleTags = useMemo(() => tagsEntities?.filter(tag => hiddenTagIds?.includes(tag.tagId) && [0,1]?.includes(tag?.level)), [tagsEntities, hiddenTagIds])

    useEffect(() => {
      const checkHeight = () => {
        //@ts-ignore
        const tagsContainer = document.querySelector('#tags-scroll-container');
  
        if (tagsContainer) {
          //@ts-ignore
          const containerHeight = tagsContainer.scrollHeight;
          //@ts-ignore
          if (containerHeight > maxTagHeight) {
            setCollapseVisible(true)
          } else {
            setCollapseVisible(false);
          }
        }
      };

      const timer1 = setTimeout(checkHeight, 100);
      const timer2 = setTimeout(checkHeight, 300);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
         
    },[isLoading, visibleTags, maxTagHeight, isMobile, isSmallMobile, folders]);

    const hide = useMemo(() => {
      // return (filter === null || !tagsEntities || tagsEntities?.length === 0 || (visibleTags?.length === 0 && !isMe))
      return !visible || (visibleTags?.length === 0 && !isMe)
    } , [filter, tagsEntities, visible, visibleTags, isMe])

    useEffect(() => {
      dispatch(ProfileFoldersSlice.actions.setCloudButtonStatus(!hide))
    } , [hide, dispatch, filter, tagsEntities, visibleTags, isMe, visible])

    if (hide) return null;

    if (isLoading) return <TagWrapper isLoading/>

    return (
          <TagWrapperLocal isCGRAVE={isCGRAVE || isContinuumSite || isAdcrDafesNet}>
            <FlexBox>
                <FlexBox direction="column" directionMobile="column" gy={12} width='100%'>
                    <ScrollWrapper id={'tags-scroll-container'} ref={tagsRef} collapse={!open} isAdcrDafesNet={isAdcrDafesNet} overflowVisible={false}>
                        <BTagsGridNew style={{marginBottom: '12px'}} noLimitMaxHeight>
                          <FoldersTabs 
                            designerId={designerId}
                            setTagId={setTagId} 
                            tabs={folders}
                            update={update}
                            activeId={folderId}
                            isMe={isMe} 
                          />
                          {/* {folderComponent} */}
                          {visibleTags && visibleTags?.length > 0 && visibleTags?.map((tag: Tag) => <BTag
                              tag={tag}
                              onClick={() => onTagClick(tag.tagId)}
                              key={tag.tagId}
                              selected={tag.tagId === tagIdProp}
                              // @ts-ignore
                              educational={tag?.isEducationTag}
                              // @ts-ignore
                              organization={tag?.isTeamTag}
                              isShort
                              isTagsCloud
                          >
                              <TagsTitleWrapper gx={8}><span>{bTag.title(tag)}</span> {isEditMode && <CloseIcon/>}</TagsTitleWrapper>
                          </BTag>)}
                        </BTagsGridNew>
                    </ScrollWrapper>
                    {isEditMode && <>
                        <SplitLine/>
                        <ScrollWrapper>
                            <BTagsGridNew>
                                {invisibleTags?.map((tag: Tag) => <BTag
                                    tag={tag}
                                    onClick={() => onTagClick(tag.tagId, true)}
                                    key={tag.tagId}
                                    selected={tag.tagId === tagIdProp}
                                    isDeziiignStyle={isDeziiign}
                                    // @ts-ignore
                                    educational={tag?.isEducationTag}
                                    // @ts-ignore
                                    organization={tag?.isTeamTag}
                                    isShort
                                >
                                    <TagsTitleWrapper gx={8}><span>{bTag.title(tag)}</span> {isEditMode && <ArrowMoveTopIcon/>}</TagsTitleWrapper>
                                </BTag>)}
                            </BTagsGridNew>
                    </ScrollWrapper>
                    </>}
                </FlexBox>
                {isMe && <EditIconWrapper active={isEditMode} onClick={() => setIsEditMode(!isEditMode)}>
                    <EditIcon/>
                </EditIconWrapper>}
            </FlexBox>
            {collapseButton}
          </TagWrapperLocal>

    )
}